import React, { useEffect, useState, useMemo, useRef, useCallback, ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { usePolling } from '../context/PollingContext';
import { Check, AlertCircle, Send, AlertTriangle, Award, Trophy, Target, Plus, Minus } from 'lucide-react';
import { useUniversalTextSizing, getUniversalTextConfig } from '../utils/cardTitleSizing';
import { CardTitle, BodyText, SmallText, CaptionText, ScoreDisplay, UniversalButton, useUniversalSizing } from './UniversalText';
import { AWARDS_API, fetchWithAuth } from '../utils/api';

// Type definitions
export interface Award {
  name: string;
  color: string;
  minScore: number;
  maxScore: number;
  scaledMin?: number;
  scaledMax?: number;
}

export interface AwardsConfig {
  awards: Award[];
  isPercentageBased: boolean;
  maxPointsPerJudge: number;
  numberOfJudges: number;
}

export interface Scores {
  [criterionId: string]: number;
}

export interface SimpleScoreData {
  simpleScore: number;
}

export interface Routine {
  routineNumber?: string;
  id?: string;
  name?: string;
  performerName?: string;
  [key: string]: any;
}

export interface ContainerDimensions {
  width: number;
  height: number;
}

export type ContainerSize = 'small' | 'medium' | 'large' | 'xlarge';
export type ScoringMode = 'criteria' | 'simple';

export interface SizeConfig {
  headerSize: string;
  iconSize: string;
  scoreSize: string;
  badgeSize: string;
  cardPadding: string;
  sectionSpacing: string;
  buttonHeight: string;
  buttonTextSize: string;
  buttonIconSize: number;
  awardTextSize: string;
  emptyStateIconSize: string;
  emptyStateTitleSize: string;
  emptyStateDescSize: string;
}

export type ButtonVariant = 'loading' | 'disabled' | 'ready' | 'incomplete' | 'success' | 'resubmit' | 'submit';

export interface ButtonState {
  disabled: boolean;
  text: string;
  variant: ButtonVariant;
  icon: React.ComponentType<{ size?: number; className?: string }> | null;
}

export interface JudgeProfile {
  id: string;
  name: string;
  title?: string;
  defaultNote?: string;
  photoUrl?: string;
  assignedPosition?: number; // Judge position number
  isTeacherCritique?: boolean; // Flag to indicate if this judge is a Teacher Critique judge
}

export interface RoutineTypeConfig {
  label: string;
  typeCode: string;
  receivesNumericalScore: boolean;
  receivesTotalScoreAward: boolean;
  hasStyleScoreSheet: boolean;
  eligibleForHighScore: boolean;
  eligibleForAdjudicatedAwards: boolean;
  eligibleForSpecialAwards: boolean;
}

export interface TotalScoreCardProps {
  judgeId?: string;
  currentRoutine?: Routine | null;
  currentIndex?: number;
  scores?: Scores;
  comments?: string;
  isSubmitted?: boolean;
  lastUpdateTime?: Date | null;
  onScoreSubmit?: ((scores: Scores, comments: string, booleanFlags?: { iChoreographedThis?: boolean; notFamilyFriendly?: boolean }) => void) | null;
  onScoreUpdate?: ((scores: Scores | SimpleScoreData, isSubmitted: boolean, isComplete: boolean) => void) | null;
  scoringMode?: ScoringMode;
  className?: string;
  containerSize?: ContainerSize;
  judgeProfile?: JudgeProfile | null;
  maxHeight?: string;
  styleFeedback?: Record<string, 'good' | 'needs-work'>;
  routineTypeConfig?: RoutineTypeConfig | null;
}

export interface AwardsApiResponse {
  awards?: Award[];
  isPercentageBased?: boolean;
  maxPointsPerJudge?: number;
  numberOfJudges?: number;
}

export interface SubmitData {
  judgeId?: string;
  routineId: string;
  routineIndex: number;
  comments: string;
  totalScore: number;
  submittedTime: string;
  scores?: Scores | { simpleScore: number };
  simpleScore?: number;
  styleFeedback?: {
    good?: Array<{category: string, criteria: string}>;
    'needs-work'?: Array<{category: string, criteria: string}>;
  };
}

// Transform styleFeedback from old format to simplified format (let DynamoDB handle attribute conversion)
const transformStyleFeedback = (oldFeedback: Record<string, 'good' | 'needs-work'>): {
  good?: Array<{category: string, criteria: string}>;
  'needs-work'?: Array<{category: string, criteria: string}>;
} => {
  const good: Array<{category: string, criteria: string}> = [];
  const needsWork: Array<{category: string, criteria: string}> = [];
  
  Object.entries(oldFeedback).forEach(([key, feedbackType]) => {
    // Parse the key format "categoryName:criteriaText"
    const [category, criteria] = key.split(':', 2);
    if (!category || !criteria) {
      console.warn('Invalid feedback key format:', key);
      return;
    }
    
    const feedbackItem = { category, criteria };
    
    if (feedbackType === 'good') {
      good.push(feedbackItem);
    } else if (feedbackType === 'needs-work') {
      needsWork.push(feedbackItem);
    }
  });
  
  const result: { 
    good?: Array<{category: string, criteria: string}>; 
    'needs-work'?: Array<{category: string, criteria: string}>; 
  } = {};
  
  if (good.length > 0) result.good = good;
  if (needsWork.length > 0) result['needs-work'] = needsWork;
  
  return result;
};

// Hook to detect container size instead of viewport size
const useContainerSize = (containerRef: React.RefObject<HTMLDivElement | null>) => {
  const [containerSize, setContainerSize] = useState<ContainerSize>('medium');
  const [dimensions, setDimensions] = useState<ContainerDimensions>({ width: 0, height: 0 });

  useEffect(() => {
    const updateContainerSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        const { width, height } = rect;
        
        setDimensions({ width, height });
        
        // Determine size based on container dimensions, not viewport
        if (width < 300 || height < 400) {
          setContainerSize('small');
        } else if (width < 500 || height < 600) {
          setContainerSize('medium');
        } else if (width < 800 || height < 800) {
          setContainerSize('large');
        } else {
          setContainerSize('xlarge');
        }
      }
    };

    // Use ResizeObserver for better performance than window resize
    const resizeObserver = new ResizeObserver(updateContainerSize);
    
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
      updateContainerSize(); // Initial measurement
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [containerRef]);

  return { containerSize, dimensions };
};

const TotalScoreCard: React.FC<TotalScoreCardProps> = ({ 
  judgeId,
  currentRoutine,
  currentIndex = 0,
  scores = {},
  comments = "",
  isSubmitted = false,
  lastUpdateTime = null,
  onScoreSubmit = null,
  onScoreUpdate = null,
  scoringMode = 'simple', // 'criteria' or 'simple'
  className = "",
  containerSize = 'medium', // Allow parent to override container size
  judgeProfile = null,
  maxHeight,
  styleFeedback = {},
  routineTypeConfig = null
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const containerRef = useRef<HTMLDivElement>(null);
  const { containerSize: detectedSize, dimensions } = useContainerSize(containerRef);
  
  // Use provided containerSize or detected size
  const effectiveContainerSize = containerSize !== 'medium' ? containerSize : detectedSize;
  
  // Use universal text sizing for consistency across all cards
  const { textSize } = useUniversalTextSizing();
  const universalConfig = getUniversalTextConfig(textSize);
  
  const { connected, submitScore, scoreUpdates } = usePolling();
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [isScoreModifiedAfterSubmit, setIsScoreModifiedAfterSubmit] = useState<boolean>(false);
  const [allCriteriaScored, setAllCriteriaScored] = useState<boolean>(false);
  const [simpleScore, setSimpleScore] = useState<number>(100);
  
  // New state for comments and checkboxes
  const [localComments, setLocalComments] = useState<string>(comments);
  const [notFamilyFriendly, setNotFamilyFriendly] = useState<boolean>(false);
  const [iChoreographed, setIChoreographed] = useState<boolean>(false);
  
  // Track if user has interacted with the score (to prevent accidental submissions)
  const [hasUserInteracted, setHasUserInteracted] = useState<boolean>(false);
  
  // Track if this routine has been submitted (separate from prop)
  const [isCurrentRoutineSubmitted, setIsCurrentRoutineSubmitted] = useState<boolean>(false);
  
  // Track the current routine ID to detect actual routine changes
  const [currentRoutineId, setCurrentRoutineId] = useState<string | null>(null);
  
  // Awards configuration state
  const [awardsConfig, setAwardsConfig] = useState<AwardsConfig | null>(null);
  const [awardsLoading, setAwardsLoading] = useState<boolean>(true);
  const submittedScoresRef = useRef<string | null>(null);

  // Dynamic behavior based on routine type configuration
  const shouldSubmitNoScore = useMemo(() => {
    return routineTypeConfig ? !routineTypeConfig.receivesNumericalScore : false;
  }, [routineTypeConfig]);

  const isScoringDisabled = useMemo(() => {
    return routineTypeConfig ? !routineTypeConfig.receivesNumericalScore : false;
  }, [routineTypeConfig]);

  const hasStyleFeedback = useMemo(() => {
    return routineTypeConfig ? routineTypeConfig.hasStyleScoreSheet : true;
  }, [routineTypeConfig]);

  const submitButtonText = useMemo(() => {
    return 'Submit Score'; // Always use default text now
  }, []);

  // Clear success state when routine changes
  useEffect(() => {
    const routineId = currentRoutine?.number || currentRoutine?.id;
    if (routineId && routineId !== currentRoutineId) {
      setCurrentRoutineId(routineId);
      setSuccess(false); // Clear success state when routine changes
    }
  }, [currentRoutine, currentRoutineId]);

  // Load awards configuration
  useEffect(() => {
    const loadAwards = async () => {
      try {
        setAwardsLoading(true);
        const data: AwardsApiResponse = await fetchWithAuth(AWARDS_API);
        setAwardsConfig({
          awards: data.awards || [],
          isPercentageBased: data.isPercentageBased ?? true,
          maxPointsPerJudge: data.maxPointsPerJudge || 100,
          numberOfJudges: data.numberOfJudges || 3
        });
      } catch (err) {
        console.error("Error loading awards configuration:", err);
        // Set default configuration if loading fails
        setAwardsConfig({
          awards: [],
          isPercentageBased: true,
          maxPointsPerJudge: 100,
          numberOfJudges: 3
        });
      } finally {
        setAwardsLoading(false);
      }
    };

    loadAwards();
  }, []);

  // Container-responsive sizing configuration using universal text sizing
  const sizeConfig = useMemo((): SizeConfig => {
    const configs: Record<ContainerSize, SizeConfig> = {
      small: {
        headerSize: universalConfig.smallText,
        iconSize: universalConfig.iconSize,
        scoreSize: 'text-4xl', // Increased from text-2xl
        badgeSize: universalConfig.badgeSize,
        cardPadding: 'p-2',
        sectionSpacing: 'space-y-2',
        buttonHeight: 'h-8',
        buttonTextSize: 'text-xs',
        buttonIconSize: 12,
        awardTextSize: universalConfig.smallText,
        emptyStateIconSize: universalConfig.iconSize,
        emptyStateTitleSize: universalConfig.smallText,
        emptyStateDescSize: universalConfig.captionText
      },
      medium: {
        headerSize: universalConfig.bodyText,
        iconSize: universalConfig.iconSize,
        scoreSize: 'text-6xl', // Increased from text-3xl
        badgeSize: universalConfig.badgeSize,
        cardPadding: 'p-3',
        sectionSpacing: 'space-y-3',
        buttonHeight: 'h-10',
        buttonTextSize: 'text-sm',
        buttonIconSize: 16,
        awardTextSize: universalConfig.bodyText,
        emptyStateIconSize: universalConfig.iconSize,
        emptyStateTitleSize: universalConfig.bodyText,
        emptyStateDescSize: universalConfig.smallText
      },
      large: {
        headerSize: universalConfig.bodyText,
        iconSize: universalConfig.iconSize,
        scoreSize: 'text-7xl', // Increased from text-4xl
        badgeSize: universalConfig.badgeSize,
        cardPadding: 'p-4',
        sectionSpacing: 'space-y-4',
        buttonHeight: 'h-12',
        buttonTextSize: 'text-base',
        buttonIconSize: 18,
        awardTextSize: universalConfig.bodyText,
        emptyStateIconSize: universalConfig.iconSizeLarge,
        emptyStateTitleSize: universalConfig.bodyText,
        emptyStateDescSize: universalConfig.smallText
      },
      xlarge: {
        headerSize: universalConfig.bodyText,
        iconSize: universalConfig.iconSizeLarge,
        scoreSize: 'text-8xl', // Increased from text-5xl
        badgeSize: universalConfig.badgeSize,
        cardPadding: 'p-5',
        sectionSpacing: 'space-y-5',
        buttonHeight: 'h-14',
        buttonTextSize: 'text-lg',
        buttonIconSize: 20,
        awardTextSize: universalConfig.bodyText,
        emptyStateIconSize: universalConfig.iconSizeLarge,
        emptyStateTitleSize: universalConfig.bodyText,
        emptyStateDescSize: universalConfig.smallText
      }
    };
    return configs[effectiveContainerSize] || configs.medium;
  }, [effectiveContainerSize, universalConfig]);
  
  // Calculate total score based on scoring mode
  const totalScore = useMemo((): number => {
    // If judge choreographed this routine, score should be 0
    if (iChoreographed) {
      return 0;
    }
    
    if (scoringMode === 'simple') {
      return simpleScore;
    }
    return Object.values(scores).reduce((sum: number, score) => sum + score, 0);
  }, [scores, simpleScore, scoringMode, iChoreographed]);
  
  // Calculate maximum possible score
  const maxPossibleScore = useMemo((): number => {
    // Simply return 100 as the max possible score, matching the sum of all criteria points
    return 100;
  }, [scores]);
  
  // Calculate score percentage
  const scorePercentage = useMemo((): number => {
    if (maxPossibleScore === 0) return 0;
    return Math.round((totalScore / maxPossibleScore) * 100);
  }, [totalScore, maxPossibleScore]);
  
  // Handle simple score changes
  const handleSimpleScoreChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (iChoreographed) return; // Prevent score changes when choreographed
    
    const value = Math.min(Math.max(parseFloat(event.target.value) || 0, 0), 100);
    setSimpleScore(value);
    setIsScoreModifiedAfterSubmit(true);
    setHasUserInteracted(true); // Mark as user interacted
    setSuccess(false); // Clear success state when user changes score
    
    // Notify parent component of score update
    if (onScoreUpdate) {
      onScoreUpdate({ simpleScore: value }, false, true); // Always complete since we have a valid score
    }
  }, [onScoreUpdate, iChoreographed]);

  // Handle plus/minus score adjustments
  const handleScoreIncrement = useCallback(() => {
    if (iChoreographed) return; // Prevent score changes when choreographed
    
    const newScore = Math.min(simpleScore + 1, 100);
    setSimpleScore(newScore);
    setIsScoreModifiedAfterSubmit(true);
    setHasUserInteracted(true); // Mark as user interacted
    setSuccess(false); // Clear success state when user changes score
    
    if (onScoreUpdate) {
      onScoreUpdate({ simpleScore: newScore }, false, true); // Always complete since we have a valid score
    }
  }, [simpleScore, onScoreUpdate, iChoreographed]);

  const handleScoreDecrement = useCallback(() => {
    if (iChoreographed) return; // Prevent score changes when choreographed
    
    const newScore = Math.max(simpleScore - 1, 0);
    setSimpleScore(newScore);
    setIsScoreModifiedAfterSubmit(true);
    setHasUserInteracted(true); // Mark as user interacted
    setSuccess(false); // Clear success state when user changes score
    
    if (onScoreUpdate) {
      onScoreUpdate({ simpleScore: newScore }, false, true); // Always complete since we have a valid score
    }
  }, [simpleScore, onScoreUpdate, iChoreographed]);

  // Handle comments changes
  const handleCommentsChange = useCallback((event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newComments = event.target.value;
    setLocalComments(newComments);
    setIsScoreModifiedAfterSubmit(true);
    
    // Notify parent component of comment update
    if (onScoreUpdate) {
      const currentScoreData = scoringMode === 'simple' ? { simpleScore } : scores;
      const isComplete = scoringMode === 'simple' ? true : allCriteriaScored; // Simple scoring is always complete with default 100
      onScoreUpdate(currentScoreData, false, isComplete);
    }
  }, [onScoreUpdate, scoringMode, simpleScore, scores, allCriteriaScored]);

  // Detect actual routine changes and reset form state
  useEffect(() => {
    const newRoutineId = currentRoutine?.number || currentRoutine?.id || `${currentIndex}`;
    
    // Only reset if this is actually a different routine
    if (newRoutineId !== currentRoutineId) {
      
      // Update tracked routine ID
      setCurrentRoutineId(newRoutineId);
      
      // Reset all form state for new routine
      const defaultNote = judgeProfile?.defaultNote || '';
      setLocalComments(defaultNote);
      setNotFamilyFriendly(false);
      setIChoreographed(false);
      setIsScoreModifiedAfterSubmit(false);
      setHasUserInteracted(false);
      setIsCurrentRoutineSubmitted(false);
      
      // Check for existing submitted score for this routine and judge
      if (judgeId && newRoutineId && scoreUpdates) {
        const scoreKey = `${newRoutineId}_${judgeId}`;
        const existingScore = scoreUpdates[scoreKey];
        
        if (existingScore && existingScore.isSubmitted) {
          // Load existing comments and flags
          setLocalComments(existingScore.comments || defaultNote);
          setNotFamilyFriendly(existingScore.notFamilyFriendly || false);
          setIChoreographed(existingScore.iChoreographedThis || false);
          setIsCurrentRoutineSubmitted(true); // Mark as submitted
          setHasUserInteracted(true); // Mark as interacted since it was previously submitted
          
          if (scoringMode === 'simple' && existingScore.scores?.simpleScore !== undefined) {
            setSimpleScore(existingScore.scores.simpleScore);
          } else if (scoringMode === 'simple') {
            setSimpleScore(100);
          }
        } else {
          if (scoringMode === 'simple') {
            setSimpleScore(100);
          }
        }
      } else if (scoringMode === 'simple') {
        // Fallback to default if we don't have the necessary data
        setSimpleScore(100);
      }
    }
  }, [currentRoutine, currentIndex, scoringMode, judgeProfile?.defaultNote, currentRoutineId, judgeId, scoreUpdates]);

  // Separate effect to handle initial loading on component mount
  useEffect(() => {
    
    // Only run if we haven't set a currentRoutineId yet (initial mount)
    if (currentRoutineId === null && currentRoutine && judgeId && scoreUpdates) {
      const routineId = currentRoutine?.number || currentRoutine?.id || `${currentIndex}`;
      const scoreKey = `${routineId}_${judgeId}`;
      const existingScore = scoreUpdates[scoreKey];
      
      
      if (existingScore && existingScore.isSubmitted) {
        // Load existing data on initial mount
        const defaultNote = judgeProfile?.defaultNote || '';
        setLocalComments(existingScore.comments || defaultNote);
        setNotFamilyFriendly(existingScore.notFamilyFriendly || false);
        setIChoreographed(existingScore.iChoreographedThis || false);
        setIsCurrentRoutineSubmitted(true);
        
        if (scoringMode === 'simple' && existingScore.scores?.simpleScore !== undefined) {
          setSimpleScore(existingScore.scores.simpleScore);
        }
        
        // Set the routine ID to prevent this from running again
        setCurrentRoutineId(routineId);
      }
    }
  }, [currentRoutine, currentIndex, judgeId, scoreUpdates, scoringMode, judgeProfile?.defaultNote, currentRoutineId]);

  // Additional effect to handle when scoreUpdates gets populated after initial mount
  useEffect(() => {
    // Only run if we have a currentRoutineId set but haven't loaded existing data yet
    if (currentRoutineId && currentRoutine && judgeId && scoreUpdates && !isCurrentRoutineSubmitted) {
      const routineId = currentRoutine?.number || currentRoutine?.id || currentRoutineId;
      const scoreKey = `${routineId}_${judgeId}`;
      const existingScore = scoreUpdates[scoreKey];
      
      
      if (existingScore && existingScore.isSubmitted) {
        // Load existing data
        const defaultNote = judgeProfile?.defaultNote || '';
        setLocalComments(existingScore.comments || defaultNote);
        setNotFamilyFriendly(existingScore.notFamilyFriendly || false);
        setIChoreographed(existingScore.iChoreographedThis || false);
        setIsCurrentRoutineSubmitted(true);
        setHasUserInteracted(true); // Mark as interacted since it was previously submitted
        
        if (scoringMode === 'simple' && existingScore.scores?.simpleScore !== undefined) {
          setSimpleScore(existingScore.scores.simpleScore);
        }
      }
    }
  }, [scoreUpdates, currentRoutineId, currentRoutine, judgeId, scoringMode, judgeProfile?.defaultNote, isCurrentRoutineSubmitted]);

  // Sync local comments with prop (separate from routine changes)
  useEffect(() => {
    setLocalComments(comments);
  }, [comments]);

  // Save submitted scores to compare against later changes
  useEffect(() => {
    if (isSubmitted && !isScoreModifiedAfterSubmit) {
      // Store a deep copy of the scores when submission occurs
      submittedScoresRef.current = JSON.stringify(scores);
    }
  }, [isSubmitted, isScoreModifiedAfterSubmit, scores]);
  
  // Check if scores have been modified after submission
  useEffect(() => {
    if (isSubmitted && submittedScoresRef.current) {
      const previousScores = submittedScoresRef.current;
      const currentScores = JSON.stringify(scores);
      
      // Only set modified flag if the scores actually changed
      if (previousScores !== currentScores) {
        setIsScoreModifiedAfterSubmit(true);
      }
    }
  }, [scores, isSubmitted]);
  
  // Reset the modification flag when routine changes or when resubmitted
  useEffect(() => {
    setIsScoreModifiedAfterSubmit(false);
    submittedScoresRef.current = null;
  }, [currentRoutine, currentIndex]);
  
  // Update allCriteriaScored when parent component reports it
  useEffect(() => {
    // Check for non-zero values in all scores
    const allScored = Object.values(scores).every(score => score > 0) && 
                     Object.keys(scores).length > 0;
    setAllCriteriaScored(allScored);
  }, [scores]);
  
  // Get award based on percentage using dynamic configuration
  const getScoreAward = (): Award | null => {
    if (!awardsConfig?.awards?.length) return null;

    const { awards, numberOfJudges } = awardsConfig;

    // Scale the award ranges for individual scoring (single judge)
    const scaledAwards = awards.map((award) => ({
      ...award,
      scaledMin: award.minScore / numberOfJudges,
      scaledMax: award.maxScore / numberOfJudges,
    }));

    // Find the appropriate award based on scaled ranges
    const award = scaledAwards.find(
      award => scorePercentage >= award.scaledMin! && scorePercentage <= award.scaledMax!
    );
    
    return award || scaledAwards[scaledAwards.length - 1]; // Default to lowest award
  };
  
  // Get color based on score percentage - use single color
  const getScoreColor = (): string => {
    return 'text-blue-500 dark:text-blue-400';
  };

  // Handle score submission
  const handleSubmit = async (): Promise<void> => {
    if (!currentRoutine) {
      setError("No routine selected");
      return;
    }
    
    setLoading(true);
    
    try {
      const routineId = currentRoutine.number || currentRoutine.id;
      
      if (!routineId) {
        setError("Invalid routine ID");
        return;
      }

      // Prepare scores based on routine type configuration
      let scoresForSubmission;
      
      if (shouldSubmitNoScore) {
        // For non-scoring categories, submit special "no score" value
        scoresForSubmission = scoringMode === 'simple' 
          ? { simpleScore: -1 } // Use -1 to indicate "no score"
          : {};
      } else {
        // Normal scoring validation and submission
        if (scoringMode === 'simple') {
          if (simpleScore === null || simpleScore === undefined) {
            setError("No scores to submit");
            return;
          }
          scoresForSubmission = { simpleScore: iChoreographed ? 0 : simpleScore };
        } else {
          if (Object.keys(scores).length === 0) {
            setError("No scores to submit");
            return;
          }
          if (!allCriteriaScored) {
            setError("Please assign scores to all criteria");
            return;
          }
          scoresForSubmission = scores;
        }
      }
      
      // Send the final submission through polling API (this saves to competitionScores table)
      if (connected && submitScore && judgeId) {
        
        const submitData = {
          judgeId,
          routineId,
          routineIndex: currentIndex,
          comments: localComments,
          totalScore: iChoreographed ? 0 : totalScore, // Set totalScore to 0 if choreographed
          submittedTime: new Date().toISOString(),
          scores: scoresForSubmission,
          isSubmitted: true,
          iChoreographedThis: iChoreographed, // Use backend expected field name
          notFamilyFriendly: notFamilyFriendly, // Add notFamilyFriendly flag
          routineTypeCode: routineTypeConfig?.typeCode,
          isNoScore: shouldSubmitNoScore,
          routineTypeConfig: routineTypeConfig, // Include full config for backend processing
          // Include current routine data to avoid backend lookup
          routineData: currentRoutine ? {
            routineNumber: String(currentRoutine.number || currentRoutine.routineNumber || routineId),
            routineId: String(currentRoutine.id || routineId),
            name: currentRoutine.name || currentRoutine.title || `Routine ${routineId}`,
            performerName: currentRoutine.performerName || '',
            studioCode: currentRoutine.studioCode || '',
            division: currentRoutine.division || '',
            ageGroup: currentRoutine.ageGroup || '',
            category: currentRoutine.category || '',
            dancersCount: String(currentRoutine.dancersCount || '')
          } : undefined,
          ...(hasStyleFeedback && styleFeedback && Object.keys(styleFeedback).length > 0 && { 
            styleFeedback: transformStyleFeedback(styleFeedback) 
          })
        };

        await submitScore(submitData);
        
        // Store the submitted scores
        submittedScoresRef.current = JSON.stringify(scores);
        
        setError(null);
        setIsScoreModifiedAfterSubmit(false);
        
        // Show success state - stays until user changes score or routine changes
        setSuccess(true);
      } else {
        setError("Not connected to server");
        return;
      }
      
      // Also call the parent component's callback if provided (for polling/communication)
      if (onScoreSubmit) {
        onScoreSubmit(scoresForSubmission, localComments, {
          iChoreographedThis: iChoreographed,
          notFamilyFriendly: notFamilyFriendly
        });
      }
      
    } catch (err) {
      console.error('Failed to submit scores:', err);
      
      const errorMessage = err instanceof Error ? err.message : "Failed to submit scores";
      setError(errorMessage);
      setSuccess(false);
    } finally {
      setLoading(false);
    }
  };

  // Get current award
  const currentAward = getScoreAward();

  // Get button state and content based on current conditions
  const getButtonState = (): ButtonState => {
    
    if (loading) {
      return {
        disabled: true,
        text: 'Submitting...',
        variant: 'loading',
        icon: null
      };
    }
    
    if (!currentRoutine) {
      return {
        disabled: true,
        text: 'No Routine Selected',
        variant: 'disabled',
        icon: AlertCircle
      };
    }
    
    if (scoringMode === 'simple') {
      if (!hasUserInteracted && !(isSubmitted || isCurrentRoutineSubmitted)) {
        return {
          disabled: true,
          text: 'Adjust Score to Continue',
          variant: 'ready',
          icon: Target
        };
      }
    } else {
      if (Object.keys(scores).length === 0) {
        return {
          disabled: true,
          text: 'Ready to Score Routine',
          variant: 'ready',
          icon: Target
        };
      }
      
      if (!allCriteriaScored) {
        return {
          disabled: true,
          text: 'Please Score All Criteria',
          variant: 'incomplete',
          icon: AlertTriangle
        };
      }
    }
    
    if (isSubmitted || isCurrentRoutineSubmitted) {
      if (!isScoreModifiedAfterSubmit) {
        return {
          disabled: true,
          text: 'Score Submitted Successfully',
          variant: 'success',
          icon: Check
        };
      } else {
        return {
          disabled: false,
          text: 'Resubmit Scores',
          variant: 'resubmit',
          icon: Send
        };
      }
    }
    
    return {
      disabled: false,
      text: 'Submit Final Scores',
      variant: 'submit',
      icon: Send
    };
  };

  const buttonState = getButtonState();

  return (
    <div 
      ref={containerRef} 
      className={`${className} h-full flex flex-col overflow-hidden`}
      style={{
        maxHeight: maxHeight || 'calc(100vh - 100px)',
        minHeight: '200px'
      }}
    >
      
      {!currentRoutine ? (
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center text-white/60 flex-1 flex flex-col items-center justify-center"
        >
          <div className="mb-4">
            <Trophy className={`${sizeConfig.emptyStateIconSize} mx-auto text-purple-400 opacity-50`} />
          </div>
          <BodyText className="font-medium">No routine selected</BodyText>
          <SmallText className="text-white/40 mt-2">Select a routine to view total score</SmallText>
        </motion.div>
      ) : (
        <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
          {/* Header - Compact */}
          <motion.div 
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="flex-shrink-0 pb-2"
          >
            <CardTitle className={`flex items-center ${universalConfig.spacing} text-sm`}>
              <Target className={`w-4 h-4 text-purple-400`} />
              TOTAL SCORE
            </CardTitle>
          </motion.div>


          {/* Main Content - Dynamic based on routine type */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="flex-1 flex flex-col items-center justify-center min-h-0 py-2"
          >
            {/* Always show normal scoring interface, but adapt based on routine type */}
            <div className="flex items-center justify-center gap-4">
              {/* Minus Button - Smaller */}
              <button
                onClick={handleScoreDecrement}
                disabled={simpleScore <= 0 || iChoreographed || isScoringDisabled}
                className="w-8 h-8 rounded-full bg-red-500/20 border border-red-400/40 text-red-400 hover:bg-red-500/30 hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200"
              >
                <Minus size={16} />
              </button>

              {/* Score Display - Appropriately Sized */}
              <div className="text-center flex-1 max-w-xs">
                <div 
                  className="font-black text-white leading-none drop-shadow-2xl"
                  style={{
                    fontSize: `clamp(1.5rem, ${effectiveContainerSize === 'small' ? '2.5vw' : effectiveContainerSize === 'medium' ? '2.2vw' : effectiveContainerSize === 'large' ? '2vw' : '1.8vw'}, 2.5rem)`,
                    textShadow: '0 0 20px rgba(255, 255, 255, 0.3)'
                  }}
                >
                  {isScoringDisabled ? 'NO SCORE' : (iChoreographed ? 0 : Math.round(simpleScore))}
                </div>
                <div className="text-white/60 text-xs font-medium mt-1 tracking-wider">
                  {iChoreographed ? 'CHOREOGRAPHER' : (isScoringDisabled ? 'NO NUMERICAL SCORE' : 'OUT OF 100')}
                </div>
              </div>

              {/* Plus Button - Smaller */}
              <button
                onClick={handleScoreIncrement}
                disabled={simpleScore >= 100 || iChoreographed || isScoringDisabled}
                className="w-8 h-8 rounded-full bg-green-500/20 border border-green-400/40 text-green-400 hover:bg-green-500/30 hover:text-green-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-all duration-200"
              >
                <Plus size={16} />
              </button>
            </div>
          </motion.div>

          {/* Bottom Section - Always show checkboxes and comments */}
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex-shrink-0 space-y-2 pt-1"
          >
            {/* Checkboxes - Very Minimal */}
            <div className="flex flex-row gap-4 justify-center">
              {/* Not Family Friendly Checkbox */}
              <div className="flex items-center gap-1">
                <div className="relative">
                  <input
                    type="checkbox"
                    id="notFamilyFriendly"
                    checked={notFamilyFriendly}
                    onChange={(e) => setNotFamilyFriendly(e.target.checked)}
                    className="sr-only"
                  />
                  <label
                    htmlFor="notFamilyFriendly"
                    className={`w-3 h-3 rounded border cursor-pointer flex items-center justify-center transition-all duration-200 ${
                      notFamilyFriendly
                        ? 'bg-purple-500 border-purple-400 text-white'
                        : 'bg-transparent border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    {notFamilyFriendly && <Check size={8} />}
                  </label>
                </div>
                <div className="text-white/50 font-medium cursor-pointer text-xs" onClick={() => setNotFamilyFriendly(!notFamilyFriendly)}>
                  NOT FAMILY FRIENDLY
                </div>
              </div>

              {/* I Choreographed This Routine Checkbox */}
              <div className="flex items-center gap-1">
                <div className="relative">
                  <input
                    type="checkbox"
                    id="iChoreographed"
                    checked={iChoreographed}
                    onChange={(e) => setIChoreographed(e.target.checked)}
                    className="sr-only"
                  />
                  <label
                    htmlFor="iChoreographed"
                    className={`w-3 h-3 rounded border cursor-pointer flex items-center justify-center transition-all duration-200 ${
                      iChoreographed
                        ? 'bg-purple-500 border-purple-400 text-white'
                        : 'bg-transparent border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    {iChoreographed && <Check size={8} />}
                  </label>
                </div>
                <div className="text-white/50 font-medium cursor-pointer text-xs" onClick={() => setIChoreographed(!iChoreographed)}>
                  I CHOREOGRAPHED THIS
                </div>
              </div>
            </div>

            {/* Notes Section - Very Minimal */}
            <div>
              <textarea
                value={localComments}
                onChange={handleCommentsChange}
                placeholder="Optional notes..."
                rows={1}
                className="w-full bg-gray-800/20 border border-gray-700/30 rounded-md px-2 py-1 text-xs text-white/70 placeholder-gray-600 focus:border-purple-400/40 focus:outline-none focus:ring-1 focus:ring-purple-400/10 resize-none transition-all duration-200"
              />
            </div>

            {/* Submit Button - At the Bottom */}
            <div>
              {error && (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex items-center justify-center gap-2 p-2 rounded-lg bg-gradient-to-r from-red-500/20 to-pink-500/20 border border-red-400/40 text-red-200 backdrop-blur-xl mb-2"
                >
                  <AlertCircle size={14} className="flex-shrink-0" />
                  <div className="font-medium text-xs">{error}</div>
                </motion.div>
              )}
              
              <button
                onClick={handleSubmit}
                disabled={success}
                className={`w-full h-9 rounded-xl font-bold text-sm transition-all duration-200 text-white shadow-lg hover:shadow-xl ${
                    success
                    ? 'bg-gradient-to-r from-green-500 to-green-600 cursor-not-allowed'
                    : 'bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700'
                }`}
              >
                {success ? (
                  <div className="flex items-center justify-center space-x-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>SUBMITTED!</span>
                  </div>
                ) : (
                  shouldSubmitNoScore 
                    ? `${submitButtonText.toUpperCase()} (NO SCORE)`
                    : `${submitButtonText.toUpperCase()} (Score: ${iChoreographed ? 0 : simpleScore})`
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TotalScoreCard;
